import React, { useState, useEffect } from "react";
import ShareBtn, { ShareBtnProps } from "../../../dynamic/ShareButton/src";

import "../../CircuitLandingPage/index.scss";
import "./olympics-heading.scss";
import {
  checkImage,
  getMediaDevice,
  imgCrop,
  stylesToString,
  hexToRGBA
} from "../../../helpers";

interface link {
  title: string;
  linkURL: string;
  linkTarget: string;
  logoImage?: string;
}

interface Props {
  heading: string;
  subheading: string;
  hostNation: string;
  liveURL: string;
  streamURL: string;
  share: ShareBtnProps;
  surface: string;
  secondaryNav: link[];
  brandingLogoURL: string;
  backgroundImageURL: imgCrop[];
  languages?: link[];
  quicklinks?: link[];
  storyTellerCategories?: string[];
  storyTellerDisplayLimit?: number;
  backgroundFillColor?: string;
  highlightColor?: string;
  nonFillTextColor?: string;
  brandColorPrimary?: string;
  brandColorSecondary?: string;
  brandColorTertiary?: string;
}

const LOCALES = {
  en: "English",
  es: "Espanol",
  fr: "French"
};

export default ({
  heading,
  subheading,
  share,
  languages,
  secondaryNav,
  brandColorPrimary = "",
  brandColorSecondary = "",
  brandColorTertiary = "",
  backgroundImageURL = [],
  brandingLogoURL = "unset",
  backgroundFillColor = "",
  highlightColor = "",
  nonFillTextColor = ""
}: // quicklinks
Props) => {
  const [navVisible, setNavVisible] = useState(false);
  const isOlympics = () =>
    location.href.indexOf("olymp") >= 0 || location.href.indexOf("olimp") >= 0;
  const isMobile = getMediaDevice() === "MOBILE";

  const getRouteName = (link: string) => {
    return link.toLocaleLowerCase().split(" ")[0];
  };

  const isActiveLocale = (locale: string) =>
    location.pathname.indexOf(`/${locale}/`) >= 0;

  let styles = {
    "--brandColor--primary": brandColorPrimary,
    "--brandColor--secondary": brandColorSecondary,
    "--brandColor--tertiary": brandColorTertiary,
    "--bannerBackgroundColor": backgroundFillColor,
    "--bannerHighlightColor": highlightColor,
    "--bannerHighlightColor--para": highlightColor,
    "--bannerTextColor": nonFillTextColor,
    "--bannerTextColor--para": nonFillTextColor,
    "--bannerImageBackground": checkImage({
      images: backgroundImageURL,
      size: "Large"
    }),
    "--bannerImageBackgroundMobile": checkImage({
      images: backgroundImageURL,
      size: "Small"
    })
  };

  const chevronColorKeys = [
    "--primary",
    "--secondary",
    "--tertiary",
    "bannerNavTextColorTour"
  ];

  let groomedStyles = Object.keys(styles).reduce((obj, key) => {
    if (styles[key] && styles[key] !== "no-image-set-for-this-viewport") {
      obj[key] = `${key.indexOf("Color") >= 0 ? "#" : ""}${
        key.indexOf("ImageBackground") >= 0
          ? `url(${styles[key]})`
          : styles[key]
      }`;

      if (chevronColorKeys.some(k => key.indexOf(k) >= 0)) {
        obj[`${key}-rgb`] = `${hexToRGBA(styles[key])}`;
      }
    }
    return obj;
  }, {});

  useEffect(() => {
    if (document.querySelector("head") !== null) {
      var styleNode = document.createElement("style");
      styleNode.innerHTML = `:root{${stylesToString(groomedStyles)}}`;
      // @ts-ignore
      document.querySelector("head").appendChild(styleNode);
    }
  }, []);

  return (
    <div>
      <section className="page-section olympics-heading-wrap">
        <div className="page-section__inner">
          <div
            // @ts-ignore
            style={groomedStyles}
            className={`olympics-heading ${
              isOlympics() || !!backgroundFillColor ? "olympics" : "paralympics"
            }`}
          >
            <div className="olympics-heading__info">
              <div
                className="olympics-heading__logo"
                style={{ backgroundImage: `url("${brandingLogoURL}")` }}
              />
              <div className="olympics-heading__details">
                {heading && heading.length > 0 ? <h3>{heading}</h3> : null}
                {subheading && subheading.length > 0 ? (
                  <div>{subheading}</div>
                ) : null}
              </div>
              {share ? (
                <div className="olympics-heading__share">
                  <ShareBtn {...share} />
                </div>
              ) : null}
              {languages && languages.length > 0 ? (
                <div className="olympics-heading__locales">
                  {languages &&
                    languages
                      .sort((a, b) => {
                        const isActiveA = isActiveLocale(a.title.toLowerCase());
                        const isActiveB = isActiveLocale(b.title.toLowerCase());
                        if (isActiveA && !isActiveB) {
                          return -1;
                        } else if (!isActiveA && isActiveB) {
                          return 1;
                        } else {
                          return 0;
                        }
                      })
                      .map(locale => (
                        <a
                          target={locale.linkTarget}
                          href={locale.linkURL}
                          className={`${
                            isActiveLocale(locale.title) ? "active" : ""
                          }`}
                        >
                          {LOCALES[locale.title.toLocaleLowerCase()]}
                        </a>
                      ))}
                </div>
              ) : null}
            </div>
            <div className="olympics-heading__nav">
              {secondaryNav
                .filter((_, index) => (isMobile ? index < 3 : index >= 0))
                .map(link => (
                  <a
                    href={link.linkURL}
                    target={link.linkTarget ? link.linkTarget : "_self"}
                    key={link.title}
                    className={`link-${getRouteName(link.title)} ${
                      link.linkURL.indexOf(location.pathname) >= 0
                        ? "active"
                        : ""
                    }`}
                  >
                    {link.title.split(" ").map(text => (
                      <span>{text}</span>
                    ))}
                  </a>
                ))}
              {isMobile && (
                <>
                  <a
                    className={"link-more"}
                    onClick={e => {
                      e.preventDefault();
                      setNavVisible(!navVisible);
                    }}
                  >
                    More
                  </a>
                  {navVisible && secondaryNav.length > 3 && (
                    <div className="olympics-heading__nav--submenu">
                      {secondaryNav.slice(3).map(link => (
                        <a
                          href={link.linkURL}
                          target={link.linkTarget ? link.linkTarget : "_self"}
                          key={link.title}
                          className={`sub-link ${
                            link.linkURL.indexOf(location.pathname) >= 0
                              ? "active"
                              : ""
                          }`}
                        >
                          {link.title}
                        </a>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
